import { formatCurrency } from '@if/core/utils'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import { useComputeYtdIncome } from '@src/data/api/income-calculator/income-calculator-api'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface YtdFormValues {
  ytdGross: string
  hiringDate: string
  payPeriodEndDate: string
}

const YtdCalculatorTab = () => {
  const { t } = useTranslation()
  const [computeYtdIncome, isPending, data] = useComputeYtdIncome()
  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<YtdFormValues>({
    mode: 'onChange',
    defaultValues: { ytdGross: '', hiringDate: '', payPeriodEndDate: '' },
  })

  const hiringDate = watch('hiringDate')

  const handleCalculate = handleSubmit(async (values) => {
    await computeYtdIncome({ ytdGross: parseFloat(values.ytdGross), hiringDate: values.hiringDate, payPeriodEndDate: values.payPeriodEndDate }).catch(() => undefined)
  })

  const handleCopyToClipboard = async () => {
    if (!data) return
    await navigator.clipboard.writeText(String(data.annualSalary))
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
      <Controller
        name="ytdGross"
        control={control}
        rules={{ validate: (v) => parseFloat(v) > 0 }}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('incomeCalculator.ytdGross')}
            type="number"
            size="small"
            slotProps={{ htmlInput: { min: 0 } }}
          />
        )}
      />
      <Controller
        name="hiringDate"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('incomeCalculator.hiringDate')}
            type="date"
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
          />
        )}
      />
      <Controller
        name="payPeriodEndDate"
        control={control}
        rules={{ required: true, validate: (v) => !hiringDate || v > hiringDate }}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('incomeCalculator.payPeriodEndDate')}
            type="date"
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
          />
        )}
      />
      <Button variant="contained" onClick={() => void handleCalculate()} disabled={!isValid || isPending}>
        {t('incomeCalculator.calculate')}
      </Button>
      {data && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {t('incomeCalculator.annualSalary')}: <strong>{formatCurrency(data.annualSalary)}</strong>
          </Typography>
          <Tooltip title={t('incomeCalculator.copyToClipboard')}>
            <IconButton size="small" onClick={() => void handleCopyToClipboard()}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  )
}

export default YtdCalculatorTab
